import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Download, ArrowRight } from 'lucide-react';
import api from '../config/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { isAuthenticated, loading: authLoading } = useAuth();

    // Only fetch dashboard data after auth is initialized and the user is authenticated.
    useEffect(() => {
      if (authLoading) return; // wait until auth finished initializing
      if (isAuthenticated) {
        fetchDashboardData();
      }
    }, [isAuthenticated, authLoading]);

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
      
        // Verify we have a token before making the request
        const token = localStorage.getItem('firebaseToken');
        if (!token) {
          throw new Error('No authentication token found');
        }
      
        const response = await api.get('/dashboard');
        if (!response.data || !response.data.data) {
          throw new Error('Invalid dashboard data received');
        }
      
        setDashboardData(response.data.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        let errorMessage;
      
        if (err.response?.data?.error) {
          errorMessage = err.response.data.error.message || err.response.data.error;
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error. Please check your Firebase configuration.';
        } else {
          errorMessage = err.message || 'Failed to load dashboard data';
        }
      
        setError(errorMessage);
        toast.error(errorMessage);
      
        // If unauthorized, clear token and redirect to login
        if (err.response?.status === 401) {
          localStorage.removeItem('firebaseToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } finally {
        setLoading(false);
      }
    };

    const handleExportData = () => {
      toast.success('Data export initiated!');
      // Implement data export functionality
    };

    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={fetchDashboardData}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      );
    }

    const { stats, alerts, recentActivity } = dashboardData || {};

    return (
      <div className="bg-white">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="page-header-title">Dashboard</h1>
              <p className="page-header-subtitle">System overview and key metrics</p>
            </div>
            <button
              onClick={handleExportData}
              className="btn btn-secondary mt-4 sm:mt-0 inline-flex"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </button>
          </div>
        </div>

        {/* Metrics Bar */}
        {stats && (
          <div className="metrics-bar">
            <div className="metrics-grid">
              <div className="metric-item">
                <span className="metric-label">Total Clients</span>
                <span className="metric-value">{stats.totalClients}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Active Policies</span>
                <span className="metric-value">{stats.activePolicies}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Pending Claims</span>
                <span className="metric-value">{stats.pendingClaims || 0}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Total Revenue</span>
                <span className="metric-value">KES {(stats.totalRevenue || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Sections */}
        <div className="px-6 py-8 sm:px-8 space-y-8">
          
          {/* Renewal Alerts Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Renewal Alerts</h2>
                <p className="text-sm text-gray-600 mt-1">Policies requiring attention</p>
              </div>
              <Link
                to="/clients"
                className="text-slate-600 hover:text-slate-900 inline-flex items-center text-sm font-medium"
              >
                View all <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="border border-gray-200 rounded">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <span className="font-semibold text-gray-900">{alerts?.renewalAlerts || 0} Expiring Soon</span>
                <span className="text-sm text-gray-600">Due in next 30 days</span>
              </div>
              {alerts?.renewalAlerts > 0 ? (
                <div className="p-4">
                  <p className="text-sm text-gray-700">
                    {alerts.renewalAlerts} {alerts.renewalAlerts === 1 ? 'policy' : 'policies'} require attention. Check the Clients section for details.
                  </p>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-sm text-gray-600">No renewals due in the next 30 days</p>
                </div>
              )}
            </div>
          </section>

          {/* Commission Summary Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Commission Summary</h2>
                <p className="text-sm text-gray-600 mt-1">Earnings across periods</p>
              </div>
              <Link
                to="/commissions"
                className="text-slate-600 hover:text-slate-900 inline-flex items-center text-sm font-medium"
              >
                View detailed report <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-700">This Month</td>
                    <td className="text-right font-semibold text-gray-900">KES {(stats?.monthlyCommission || 0).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="text-gray-700">This Quarter</td>
                    <td className="text-right font-semibold text-gray-900">KES {(stats?.quarterlyCommission || 0).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="text-gray-700">This Year</td>
                    <td className="text-right font-semibold text-gray-900">KES {(stats?.yearlyCommission || 0).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Recent Activity Section */}
          {recentActivity?.recentClients && recentActivity.recentClients.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Recent Clients</h2>
                  <p className="text-sm text-gray-600 mt-1">Latest additions to the system</p>
                </div>
                <Link
                  to="/clients"
                  className="text-slate-600 hover:text-slate-900 inline-flex items-center text-sm font-medium"
                >
                  View all <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
              <div className="border border-gray-200 rounded overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Client Name</th>
                      <th>Vehicle</th>
                      <th>Date Added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.recentClients.map((client) => (
                      <tr key={client.id}>
                        <td className="font-medium text-gray-900">{client.name}</td>
                        <td className="text-gray-600">{client.vehicleNumber}</td>
                        <td className="text-gray-600">{new Date(client.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

        </div>
      </div>
    );

  };

  export default Dashboard;
